import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconInfoCircleOutline } from '@iwoca/icons';
import {
    PutStateRequestBody,
    fetchGetStateByStateKey,
    fetchPutState
} from '@iwoca/lapi-client/edge';
import { Button, CheckBox } from '@iwoca/orion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import AddressFields from './fields/AddressFields';
import DOBFields from './fields/DOBFields';
import NameFields from './fields/NameFields';
import {
    convertDeserialisedPersonToFormValues,
    convertSuggestionToFormValues,
    createCustomerStateWithNewOrUpdatedPerson
} from './MSHForm.helpers';
import { TFormValues, TPerson, TSuggestion } from './MSHForm.types';
import styles from './MSHModal.module.css';
import { useIsInSelfServeSplitTest } from '../../../../../customHooks/useIsInSelfServeSplitTest';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';

export const MSHForm = ({
    onCancel,
    onSubmitSuccess,
    onSubmitError,
    personBeingEdited,
    suggestionBeingConfirmed
}: {
    onCancel: () => void;
    onSubmitSuccess: () => void;
    onSubmitError: () => void;
    personBeingEdited: TPerson | undefined;
    suggestionBeingConfirmed?: TSuggestion;
}) => {
    const getDefaultFormValues = () => {
        if (personBeingEdited) {
            return convertDeserialisedPersonToFormValues(personBeingEdited);
        }
        if (suggestionBeingConfirmed) {
            return convertSuggestionToFormValues(suggestionBeingConfirmed);
        }
        return { address: { postcode: '' } };
    };

    const defaultFormValues = getDefaultFormValues();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        getValues,
        setValue,
        trigger
    } = useForm<TFormValues>({
        defaultValues: defaultFormValues
    });

    useEffect(() => {
        if (suggestionBeingConfirmed) {
            trigger();
        }
    }, [suggestionBeingConfirmed, trigger]);

    const stateKey = useStateKey();
    const isInSelfServeSplitTest = useIsInSelfServeSplitTest();

    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });

    const queryClient = useQueryClient();

    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        }
    });

    const onSubmit: SubmitHandler<TFormValues> = async (values) => {
        if (!customerState) return;

        // Create a payload with new person
        const newState = createCustomerStateWithNewOrUpdatedPerson(
            values,
            customerState
        );

        try {
            await saveCustomerState.mutateAsync(newState);
            tracking.ampli.drawdownMshPersonAdded();
            onSubmitSuccess();
        } catch (e) {
            console.error(e);
            onSubmitError();
        }
    };

    const isApplicantBeingEdited = personBeingEdited?.isApplicant ?? false;

    const scrollToFirstError = () => {
        requestAnimationFrame(() => {
            const firstError = document.querySelector(
                '[data-testid="formErrors"]'
            );
            firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    };

    return (
        <div className={styles.formWrap}>
            <h1>
                {personBeingEdited || suggestionBeingConfirmed ? (
                    <FormattedMessage
                        id="_.modal.major_shareholder_check_form.title_edit"
                        defaultMessage="Edit person"
                    />
                ) : (
                    <FormattedMessage
                        id="_.modal.major_shareholder_check_form.title"
                        defaultMessage="Add a person"
                    />
                )}
            </h1>
            <form
                onSubmit={handleSubmit(onSubmit, scrollToFirstError)}
                className={styles.form}
            >
                <div>
                    {/* Name fields */}
                    <NameFields
                        errors={errors}
                        register={register}
                        disabled={Boolean(personBeingEdited)}
                    />

                    {/* DOB fields */}
                    <DOBFields
                        errors={errors}
                        register={register}
                        disabled={Boolean(personBeingEdited)}
                        variant="legacy"
                    />

                    {/* Roles checkboxes [Director/Shareholder/Guarantor] */}
                    <div className={`${styles.fields} ${styles.roleFields}`}>
                        <label className={styles.label}>
                            This person is a:
                        </label>
                        <CheckBox
                            {...register('director')}
                            label="Director"
                            disabled={isApplicantBeingEdited}
                        />
                        <CheckBox
                            {...register('shareholder')}
                            label="Major Shareholder (25%+ of shares or voting rights)"
                        />
                        {(personBeingEdited?.isGuarantor &&
                            !isInSelfServeSplitTest) || (
                            <p className={styles.guarantorInfo}>
                                <IconInfoCircleOutline
                                    className={styles.guarantorInfoIcon}
                                    height="20"
                                    width="20"
                                />
                                <FormattedMessage
                                    id="_.modal.major_shareholder_check_form.guarantor_hint"
                                    defaultMessage="Contact your account manager to add a guarantor"
                                />
                            </p>
                        )}
                    </div>

                    {/* Address fields */}
                    <AddressFields
                        errors={errors}
                        register={register}
                        getValues={getValues}
                        setValue={setValue}
                        disabled={isApplicantBeingEdited}
                        variant="legacy"
                    />
                </div>

                {/* Save/Cancel buttons */}
                <div className={styles.formButtonsContainer}>
                    <Button
                        variant="secondary-alt"
                        className={styles.ctaButton}
                        onClick={onCancel}
                    >
                        <FormattedMessage
                            id="_.modal.major_shareholder_check.cancel_cta"
                            defaultMessage="Cancel"
                        />
                    </Button>

                    <Button
                        variant="primary"
                        className={styles.ctaButton}
                        type="submit"
                        loading={isSubmitting}
                    >
                        <FormattedMessage
                            id="_.modal.major_shareholder_check.next_cta"
                            defaultMessage="Save"
                        />
                    </Button>
                </div>
            </form>
        </div>
    );
};
